import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { StatusPageState, StatusSummary } from '@nx-customer-apps/shared/interfaces';
import { ApplicationRoles, RemoteConfigParameter, RoutePaths, Status } from '@nx-customer-apps/shared/enums';
import { Router } from '@angular/router';
import { EnvironmentService } from './environment.service';
import { RemoteConfigService } from './remote-config.service';

@Injectable({
    providedIn: 'root'
})
export class StatusPageService {
    private http = inject(HttpClient);
    private environmentService = inject(EnvironmentService);
    private remoteConfigService = inject(RemoteConfigService);
    private router = inject(Router);

    private hasMaintenanceAccess = false;
    private readonly apiUrl = this.environmentService.statusPage.apiUrl;
    private readonly pollingInterval = this.environmentService.statusPage.pollingInterval;
    private readonly statusPageState = new BehaviorSubject<StatusPageState>({
        statusSummary: null,
        hasMaintenanceAccess: false,
        loaded: false,
        error: null
    });

    private statusPageInterval?: number;

    /**
     * Get status page for ViGuide Planning every minute
     */
    public getStatusPage(applicationRoles: ApplicationRoles[] = []): void {
        this.stopPolling();

        const hasMaintenanceAccess = this.maintenanceAllowedRoles.some(role => applicationRoles.includes(role));
        this.hasMaintenanceAccess = hasMaintenanceAccess;

        const fetchStatus = () => {
            this.http.get<StatusSummary>(this.apiUrl).subscribe({
                next: statusSummary => {
                    this.statusPageState.next({ statusSummary, hasMaintenanceAccess, loaded: true });
                    if (this.isUnavailable()) {
                        this.router.navigate([RoutePaths.ErrorUnavailable], {
                            queryParams: { status: statusSummary.status }
                        });
                    }
                },
                error: err => {
                    this.statusPageState.next({ statusSummary: null, hasMaintenanceAccess, loaded: true, error: err.message });
                }
            });
        };

        fetchStatus();
        this.statusPageInterval = setInterval(fetchStatus, this.pollingInterval) as unknown as number;
    }

    /**
     * Stop status page polling
     */
    public stopPolling(): void {
        if (this.statusPageInterval) {
            clearInterval(this.statusPageInterval);
            this.statusPageInterval = undefined;
        }
    }

    /**
     * Get status page state
     */
    public get state$(): Observable<StatusPageState> {
        return this.statusPageState.asObservable();
    }

    /**
     * Get availability status
     * @returns true if the service is unavailable (MajorOutage or (UnderMaintenance and no maintenance access))
     * @returns false if the service is operational or in partial outage
     */
    public isUnavailable(): boolean {
        const statusSummary = this.statusPageState.getValue().statusSummary;
        return statusSummary
            ? statusSummary.status === Status.MajorOutage ||
                  (statusSummary.status === Status.UnderMaintenance && !this.hasMaintenanceAccess)
            : false;
    }

    /**
     * Get current status from the status page state
     */
    public getStatus(): Status {
        return this.statusPageState.getValue().statusSummary?.status!;
    }

    private get maintenanceAllowedRoles(): ApplicationRoles[] {
        return (
            (this.remoteConfigService.getSplitedConfigParameter(RemoteConfigParameter.MaintenanceAllowedRoles) as ApplicationRoles[]) || []
        );
    }
}
